import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router';
import 'leaflet/dist/leaflet.css';
import { ApiRequestError } from './api/client';
import { endSession, keys } from './api/queries';
import { App } from './App';
import './index.css';

/** Any request answered 401 means the session is gone: drop to the login page. */
const onError = (error: Error) => {
  if (error instanceof ApiRequestError && error.status === 401 && queryClient.getQueryData(keys.session)) {
    endSession(queryClient);
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  mutationCache: new MutationCache({ onError }),
  defaultOptions: {
    queries: {
      retry: (count, error) =>
        !(error instanceof ApiRequestError && error.status < 500 && error.status > 0) && count < 2,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element');

// Makes the dashboard installable as an app (see public/sw.js). Production only: in
// development, Vite serves the files and a service worker would just get in the way.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js'));
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
